<template>
  <div>
    <b-form-group>
      <b-form-checkbox
        v-model="enabled"
        :selected="enabled"
      >
        {{ label }}
      </b-form-checkbox>
    </b-form-group>

    <div :class="{ 'disabled-content': !enabled && selectedItems.length }">
      <b-button
        variant="primary"
        size="sm"
        class="mb-1"
        :disabled="Boolean(!enabled && selectedItems.length)"
        @click="openModal"
      >
        <feather-icon
          icon="SettingsIcon"
          size="16"
          class="mr-50"
        />
        {{ $t('campaigns.openrtb.add_item') }}
      </b-button>

      <div
        v-if="selectedItems.length"
        class="selected-items"
      >
        <div
          v-for="(item, index) in selectedItems"
          :key="index"
          class="selected-item d-flex align-items-center mb-1"
        >
          <span class="mr-1">{{ item.name }}</span>
          <b-button
            variant="flat-danger"
            size="sm"
            class="ml-auto"
            @click="removeItem(index)"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </b-button>
        </div>
      </div>
    </div>

    <!-- Модальное окно выбора DSP партнера -->
    <b-modal
      v-model="showModal"
      :title="$t('campaigns.openrtb.select_item')"
      size="lg"
      hide-footer
    >
      <b-form-group>
        <div class="position-relative">
          <b-form-input
            v-model="partnerSearch"
            :placeholder="$t('campaigns.openrtb.search_partner')"
            class="mb-1"
          />
          <b-button
            v-if="partnerSearch"
            variant="link"
            class="position-absolute search-clear-btn"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </b-button>
        </div>
      </b-form-group>

      <div
        class="partners-list"
        style="max-height: 300px; overflow-y: auto;"
      >
        <div
          v-for="partner in sortedFilteredPartners"
          :key="partner._id"
          class="partner-item cursor-pointer"
          :class="{
            'partner-active': partner.status === 'active',
            'partner-inactive': partner.status === 'inactive'
          }"
          @click="togglePartnerSelection(partner._id)"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="selectedPartnerIds"
              :value="partner._id"
              class="mr-2"
              @change="togglePartnerSelection(partner._id)"
            />
            <span class="partner-name">{{ partner.name }}</span>
            <span
              v-if="partner.status === 'active'"
              class="status-badge status-active ml-auto"
            >
              Active
            </span>
            <span
              v-else
              class="status-badge status-inactive ml-auto"
            >
              Inactive
            </span>
          </div>
        </div>
      </div>

      <!-- Кнопки действий -->
      <div class="d-flex justify-content-between mt-3">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="clearSelection"
        >
          Clear Selection
        </b-button>
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-2"
            @click="closeModal"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="!selectedPartnerIds.length"
            @click="addSelectedPartners"
          >
            Add Selected ({{ selectedPartnerIds.length }})
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BFormGroup, BFormCheckbox, BFormInput, BButton, BModal,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'

export default {
  name: 'OpenRtbListBlockSimple',
  components: {
    BFormGroup,
    BFormCheckbox,
    BFormInput,
    BButton,
    BModal,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      partnerSearch: '',
      selectedPartnerIds: [], // Массив ID выбранных партнеров в модале
      partners: [],
    }
  },
  computed: {
    enabled: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:model-value', value)
      },
    },
    selectedItems: {
      get() {
        // Преобразуем массив ID в массив объектов с названиями для отображения
        if (!this.value || !Array.isArray(this.value)) return []
        return this.value.map(id => {
          const partner = this.partners.find(p => p._id === id)
          return partner ? { _id: id, name: partner.name } : { _id: id, name: 'Unknown Partner' }
        })
      },
      set(val) {
        // При изменении отправляем только массив ID
        const ids = val.map(item => item._id)
        this.$emit('input', ids)
      },
    },
    filteredPartners() {
      if (!this.partnerSearch) return this.partners
      const search = this.partnerSearch.toLowerCase()
      return this.partners.filter(p => p.name.toLowerCase().includes(search))
    },
    sortedFilteredPartners() {
      const filtered = this.filteredPartners
      return filtered.sort((a, b) => {
        // Сначала активные, потом неактивные
        if (a.status === 'active' && b.status !== 'active') return -1
        if (a.status !== 'active' && b.status === 'active') return 1
        // Если статус одинаковый, сортируем по имени
        return a.name.localeCompare(b.name)
      })
    },
  },
  watch: {
    modelValue(newValue) {
      this.$emit('enabled-change', newValue)
    },
  },
  async created() {
    await this.loadDspPartners()
  },
  methods: {
    async loadDspPartners() {
      try {
        const responseData = await useJwt.dsppartners()
        this.partners = responseData.data.dsppartners || []
      } catch (error) {
        this.partners = []
      }
    },
    addSelectedPartners() {
      // Получаем всех партнеров из выбранных чекбоксов
      const selectedPartners = this.selectedPartnerIds.map(id => {
        const partner = this.partners.find(p => p._id === id)
        return { _id: id, name: partner.name }
      })

      // Заменяем весь список выбранных партнеров
      this.selectedItems = selectedPartners
      this.showModal = false
      this.selectedPartnerIds = []
      this.partnerSearch = ''
    },
    openModal() {
      this.showModal = true
      // Устанавливаем уже выбранных партнеров как отмеченных
      this.selectedPartnerIds = [...this.value] // Копируем массив ID из value
      this.partnerSearch = ''
    },
    clearSelection() {
      this.selectedPartnerIds = []
    },
    clearSearch() {
      this.partnerSearch = ''
    },
    closeModal() {
      this.showModal = false
      this.partnerSearch = ''
    },
    isPartnerAlreadySelected(partnerId) {
      return this.selectedItems.some(item => item._id === partnerId)
    },
    togglePartnerSelection(partnerId) {
      const index = this.selectedPartnerIds.indexOf(partnerId)
      if (index > -1) {
        // Убираем из выбора
        this.selectedPartnerIds.splice(index, 1)
      } else {
        // Добавляем в выбор
        this.selectedPartnerIds.push(partnerId)
      }
    },
    removeItem(index) {
      this.selectedItems = this.selectedItems.filter((_, i) => i !== index)
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.selected-items {
  margin-top: 1rem;
}
.selected-item {
  padding: 0.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
}
.disabled-content {
  opacity: 0.5;
  pointer-events: none;
}
.partner-item {
  border: 1px solid transparent;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  padding: 10px;
  margin-bottom: 5px;

  &:hover {
    background-color: #f8f9fa;
  }
}
.partner-active {
  border-color: #28c76f;
  background-color: rgba(40, 199, 111, 0.1);
}
.partner-inactive {
  border-color: #ea5455;
  background-color: rgba(234, 84, 85, 0.1);
}
.status-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}
.status-active {
  background-color: #28c76f;
  color: white;
}
.status-inactive {
  background-color: #ea5455;
  color: white;
}
.partner-name {
  flex: 1;
}
.search-clear-btn {
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  padding: 0;
  border: none;
  background: none;
  color: #6c757d;

  &:hover {
    color: #495057;
  }
}
</style>
